(() => {
  'use strict';

  const GLOBAL_KEY = '__lounelleDeferredSectionsV6';
  const SELECTOR = '[data-lounelle-deferred-section]';
  const PREFETCH_ROOT_MARGIN = '4500px 0px';
  const HYDRATE_ROOT_MARGIN = '1800px 0px';
  const MIN_SCROLL_Y = 160;
  const MAX_ATTEMPTS = 2;
  const RETRY_DELAY = 900;
  const SCROLL_IDLE_DELAY = 140;
  const SCROLL_IDLE_MAX_WAIT = 700;

  if (window[GLOBAL_KEY]) {
    window[GLOBAL_KEY].scan();
    return;
  }

  let prefetchObserver = null;
  let hydrateObserver = null;
  let prefetchPromise = null;
  let hydrationPromise = null;
  let scrollListening = false;
  let scrollRaf = 0;
  let scrollIdleTimer = 0;
  let scrollIdleMaxTimer = 0;

  const loadedScripts = new Map();
  const parsedSections = new Map();

  const placeholders = () =>
    Array.from(document.querySelectorAll(SELECTOR)).filter(
      (element) => element.dataset.lounelleDeferredState !== 'loaded',
    );

  const sectionIds = () =>
    [...new Set(placeholders().map((element) => element.dataset.lounelleDeferredSection).filter(Boolean))];

  function buildSectionUrl(id) {
    const url = new URL(window.location.href);
    url.hash = '';
    url.searchParams.delete('sections');
    url.searchParams.delete('section_id');

    if (url.hostname !== 'shopifypreview.com' && !url.hostname.endsWith('.shopifypreview.com')) {
      const shopify = window.Shopify;
      if (shopify?.previewMode && shopify?.theme?.role !== 'main' && shopify?.theme?.id) {
        url.searchParams.set('preview_theme_id', String(shopify.theme.id));
      }
    }

    url.searchParams.set('section_id', id);
    return url.toString();
  }

  async function fetchSection(id) {
    const response = await fetch(buildSectionUrl(id), {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {
        Accept: 'text/html',
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    if (!response.ok) {
      throw new Error(`Section Rendering API returned ${response.status} for ${id}`);
    }

    return response.text();
  }

  function parseSection(id, html) {
    if (typeof html !== 'string' || !html.trim()) return null;

    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const parsedRoot = parsed.getElementById(`shopify-section-${id}`);
    if (!parsedRoot) return null;
    if (parsedRoot.querySelector(SELECTOR)) return null;

    return parsedRoot;
  }

  async function fetchValidatedSection(id) {
    let lastError = null;

    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt += 1) {
      try {
        const html = await fetchSection(id);
        const parsedRoot = parseSection(id, html);
        if (!parsedRoot) {
          throw new Error(`Invalid deferred section response for ${id}`);
        }
        return parsedRoot;
      } catch (error) {
        lastError = error;
        if (attempt < MAX_ATTEMPTS) {
          await new Promise((resolve) => window.setTimeout(resolve, RETRY_DELAY));
        }
      }
    }

    throw lastError;
  }

  function ensureExternalScript(src) {
    if (!src) return Promise.resolve();

    const absoluteSrc = new URL(src, window.location.href).href;
    if (Array.from(document.scripts).some((script) => script.src === absoluteSrc)) return Promise.resolve();
    if (loadedScripts.has(absoluteSrc)) return loadedScripts.get(absoluteSrc);

    const promise = new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = absoluteSrc;
      script.defer = true;
      script.dataset.lounelleDeferredDependency = 'true';
      script.onload = resolve;
      script.onerror = resolve;
      document.head.appendChild(script);
    });

    loadedScripts.set(absoluteSrc, promise);
    return promise;
  }

  function yieldToMainThread() {
    if (window.scheduler?.yield) {
      return window.scheduler.yield();
    }

    return new Promise((resolve) => window.setTimeout(resolve, 0));
  }

  function waitForScrollIdle() {
    return new Promise((resolve) => {
      let resolved = false;

      const cleanup = () => {
        window.removeEventListener('scroll', reset, { passive: true });
        window.removeEventListener('touchmove', reset, { passive: true });
        if (scrollIdleTimer) window.clearTimeout(scrollIdleTimer);
        if (scrollIdleMaxTimer) window.clearTimeout(scrollIdleMaxTimer);
        scrollIdleTimer = 0;
        scrollIdleMaxTimer = 0;
      };

      const done = () => {
        if (resolved) return;
        resolved = true;
        cleanup();
        resolve();
      };

      const reset = () => {
        if (scrollIdleTimer) window.clearTimeout(scrollIdleTimer);
        scrollIdleTimer = window.setTimeout(done, SCROLL_IDLE_DELAY);
      };

      window.addEventListener('scroll', reset, { passive: true });
      window.addEventListener('touchmove', reset, { passive: true });
      reset();
      scrollIdleMaxTimer = window.setTimeout(done, SCROLL_IDLE_MAX_WAIT);
    });
  }

  async function hydrateSection(id, parsedRoot) {
    const currentRoot = document.getElementById(`shopify-section-${id}`);
    if (!currentRoot || !parsedRoot) return false;

    const externalScripts = Array.from(parsedRoot.querySelectorAll('script[src]')).map((script) => {
      const src = script.getAttribute('src');
      script.remove();
      return src;
    });

    currentRoot.replaceWith(parsedRoot);

    const newRoot = document.getElementById(`shopify-section-${id}`);
    if (!newRoot) return false;

    newRoot.dataset.lounelleDeferredLoaded = 'true';
    await Promise.all(externalScripts.map((src) => ensureExternalScript(src)));

    await yieldToMainThread();

    newRoot.dispatchEvent(
      new CustomEvent('shopify:section:load', {
        bubbles: true,
        detail: { sectionId: id },
      }),
    );

    return true;
  }

  function setState(state) {
    placeholders().forEach((element) => {
      element.dataset.lounelleDeferredState = state;
    });
  }

  function disconnectObservers() {
    prefetchObserver?.disconnect();
    prefetchObserver = null;
    hydrateObserver?.disconnect();
    hydrateObserver = null;
  }

  function disarmTriggers() {
    disconnectObservers();

    if (scrollListening) {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('touchmove', onScroll);
      scrollListening = false;
    }

    if (scrollRaf) {
      window.cancelAnimationFrame(scrollRaf);
      scrollRaf = 0;
    }
  }

  function isNear(margin) {
    return placeholders().some((placeholder) => {
      const rect = placeholder.getBoundingClientRect();
      return rect.top <= window.innerHeight + margin;
    });
  }

  function shouldPrefetch() {
    return window.scrollY >= MIN_SCROLL_Y && isNear(4500);
  }

  function shouldHydrate() {
    return window.scrollY >= MIN_SCROLL_Y && isNear(1800);
  }

  async function startPrefetch() {
    if (prefetchPromise) return prefetchPromise;

    const ids = sectionIds();
    if (!ids.length) return;

    setState('prefetching');

    prefetchPromise = (async () => {
      try {
        const roots = await Promise.all(ids.map((id) => fetchValidatedSection(id)));
        ids.forEach((id, index) => parsedSections.set(id, roots[index]));
        setState('prefetched');
        return true;
      } catch (error) {
        setState('error');
        console.warn('[Lounelle] Deferred collections could not be prefetched.', error);
        return false;
      }
    })();

    return prefetchPromise;
  }

  async function hydratePending() {
    if (hydrationPromise) return hydrationPromise;

    const ids = sectionIds();
    if (!ids.length) {
      disarmTriggers();
      return;
    }

    hydrationPromise = (async () => {
      const prefetched = await startPrefetch();
      if (!prefetched) return;

      await waitForScrollIdle();

      try {
        setState('loading');

        for (let index = 0; index < ids.length; index += 1) {
          await yieldToMainThread();
          const hydrated = await hydrateSection(ids[index], parsedSections.get(ids[index]));
          if (!hydrated) {
            throw new Error(`Unable to hydrate deferred section ${ids[index]}`);
          }
          await yieldToMainThread();
        }

        parsedSections.clear();
        disarmTriggers();
      } catch (error) {
        setState('error');
        console.warn('[Lounelle] Deferred collections could not be loaded.', error);
      }
    })().finally(() => {
      hydrationPromise = null;
    });

    return hydrationPromise;
  }

  function onScroll() {
    if (scrollRaf) return;

    scrollRaf = window.requestAnimationFrame(() => {
      scrollRaf = 0;
      if (shouldPrefetch()) startPrefetch();
      if (shouldHydrate()) hydratePending();
    });
  }

  function armScrollFallback() {
    if (scrollListening) return;
    scrollListening = true;
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('touchmove', onScroll, { passive: true });
  }

  function armObservers(pending) {
    if (!('IntersectionObserver' in window)) return;

    prefetchObserver?.disconnect();
    prefetchObserver = new IntersectionObserver(
      (entries) => {
        if (window.scrollY >= MIN_SCROLL_Y && entries.some((entry) => entry.isIntersecting)) {
          startPrefetch();
        }
      },
      { root: null, rootMargin: PREFETCH_ROOT_MARGIN, threshold: 0 },
    );

    hydrateObserver?.disconnect();
    hydrateObserver = new IntersectionObserver(
      (entries) => {
        if (window.scrollY >= MIN_SCROLL_Y && entries.some((entry) => entry.isIntersecting)) {
          hydratePending();
        }
      },
      { root: null, rootMargin: HYDRATE_ROOT_MARGIN, threshold: 0 },
    );

    pending.forEach((placeholder) => {
      prefetchObserver.observe(placeholder);
      hydrateObserver.observe(placeholder);
    });
  }

  function scan() {
    const pending = placeholders().filter(
      (element) =>
        element.dataset.lounelleDeferredState !== 'loading' &&
        element.dataset.lounelleDeferredState !== 'prefetching',
    );

    if (!pending.length && !prefetchPromise && !hydrationPromise) {
      disarmTriggers();
      return;
    }

    armScrollFallback();

    if (shouldPrefetch()) startPrefetch();
    if (shouldHydrate()) hydratePending();

    if (pending.length) armObservers(pending);
  }

  window[GLOBAL_KEY] = {
    scan,
    prefetch: startPrefetch,
    hydrate: hydratePending,
  };

  window.addEventListener('online', () => {
    placeholders().forEach((element) => {
      if (element.dataset.lounelleDeferredState === 'error') {
        delete element.dataset.lounelleDeferredState;
      }
    });
    prefetchPromise = null;
    parsedSections.clear();
    scan();
  });

  document.addEventListener('shopify:section:load', scan);
  scan();
})();